import * as yup from 'yup';
import { useCallback, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FieldValues, UseFormMethods } from 'react-hook-form';
import ddt from '@wix/da-ddt';
import {
  SubmitFormValues,
  InitResponse,
  PresetsInitResponse,
} from '../redux/types';
import {
  FieldValidator,
  SubmitFormParams,
  SubmitAdapter,
  SubmitAdaptersMap,
  FieldValidationSchemasMap,
} from '../types';

const logger = ddt.createLogger('submit');

export function useSubmitFormAdapters(
  initResponse: InitResponse | PresetsInitResponse | undefined,
  params: SubmitFormParams
) {
  const { t } = useTranslation();
  const submitAdaptersRef = useRef<SubmitAdaptersMap>({});
  const fieldSchemasRef = useRef<FieldValidationSchemasMap>({});
  const schemaRef = useRef(yup.object());
  const [validationSchema, setValidationSchema] = useState<yup.ObjectSchema>();

  // Registry for nested forms
  const nestedFormsRef = useRef<Map<string, UseFormMethods<any>>>(new Map());

  const registerSubmitAdapter = useCallback(
    (name: string, adapter: SubmitAdapter) => {
      logger.log('Registering submit adapter:', name);
      submitAdaptersRef.current[name] = adapter;
    },
    []
  );

  const unregisterSubmitAdapter = useCallback((name: string) => {
    logger.log('Unregistering submit adapter:', name);
    delete submitAdaptersRef.current[name];
  }, []);

  // We use both useRef and useState to manage the validation schema.
  // The ref allows us to accumulate changes across multiple calls to `registerValidator`
  // that will happen in the same render tick, as React batches useState updates.
  const registerValidator = useCallback(
    (name: string, validator: FieldValidator) => {
      logger.log('Registering field validator:', name);
      const fieldSchema = validator({ initResponse: initResponse!, params }, t);
      fieldSchemasRef.current[name] = fieldSchema;
      schemaRef.current = schemaRef.current.concat(fieldSchema);
      setValidationSchema(schemaRef.current);
    },
    [initResponse, params, t]
  );

  const unregisterValidator = useCallback((name: string) => {
    logger.log('Unregistering validator:', name);
    delete fieldSchemasRef.current[name];
    schemaRef.current = Object.values(fieldSchemasRef.current).reduce(
      (accumulator, fieldSchema) =>
        fieldSchema ? accumulator.concat(fieldSchema) : accumulator,
      yup.object()
    );
    setValidationSchema(schemaRef.current);
  }, []);

  // Nested forms management
  const registerNestedForm = useCallback(
    <T extends FieldValues>(name: string, formMethods: UseFormMethods<T>) => {
      logger.log('Registering nested form:', name);
      nestedFormsRef.current.set(name, formMethods);
    },
    []
  );

  const unregisterNestedForm = useCallback((name: string) => {
    logger.log('Unregistering nested form:', name);
    nestedFormsRef.current.delete(name);
  }, []);

  const hasNestedForms = useCallback(() => {
    return nestedFormsRef.current.size > 0;
  }, []);

  const submitAllNestedForms = useCallback(async () => {
    const results: Array<{ name: string; data: any }> = [];

    // Convert Map entries to array to avoid iterator issues
    const nestedFormEntries = Array.from(nestedFormsRef.current.entries());

    for (const [name, formMethods] of nestedFormEntries) {
      // Validate the nested form first
      const isValid = await formMethods.trigger();
      if (!isValid) {
        throw new Error(`Nested form ${name} validation failed`);
      }
      // Get the data from the nested form
      const data = formMethods.getValues();
      results.push({ name, data });
    }

    return results;
  }, []);

  const submitAdapter = useCallback(
    (data: SubmitFormValues, formState) => {
      // Some values we just pass through to the saga
      // TODO: Find a better place to do it
      let submitData = {
        deviationId: params.deviationId,
      };

      // Append nested forms data
      const nestedFormNames = Array.from(nestedFormsRef.current.keys());
      for (const formName of nestedFormNames) {
        const formData = data[formName];
        submitData[formName] = formData;
      }

      // Apply submit adapters
      const allAdapters = Object.values(submitAdaptersRef.current);
      submitData = allAdapters.reduce(
        (accumulator, adapter) => ({
          ...accumulator,
          ...adapter(data, formState),
        }),
        submitData
      );

      // Because PAPI receives data in x-www-form-urlencoded format instead of json, empty arrays don't convert well
      // so we need to replace them with a string which allows PAPI to interpret them as such.
      for (const name in submitData) {
        const value = submitData[name];
        submitData[name] =
          Array.isArray(value) && value.length === 0 ? '_empty' : value;
      }

      return submitData;
    },
    [params]
  );

  return {
    registerValidator,
    registerSubmitAdapter,
    unregisterValidator,
    unregisterSubmitAdapter,
    submitAdapter,
    validationSchema,
    registerNestedForm,
    unregisterNestedForm,
    hasNestedForms,
    submitAllNestedForms,
  };
}
